import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { Menu, MenuItem } from '@mui/material';
import { CardImageEnum, EvoachButton } from '@evoach/ui-components';

import { Asset } from '../../../entities';
import { AssetType } from '../assetHelper';
import { AssetUploadDialog } from '../../reception/assets/AssetUploadDialog';
import { AssetSelectorDialog } from '../../reception/assets/AssetSelectorDialog';
import { AssetExternalSourceDialog } from '../../reception/assets/AssetExternalSourceDialog';

interface AssetMenuProps {
  /**
   * filter for the asset type like image, audio or video
   */
  assetType: AssetType;
  /**
   * updatePayloadValue is called when external url src or
   * assetid are changed. It is calling a function with a signature like
   * this:
   *
   * (val: string, key: 'assetid' | 'src') => {}
   *
   * If assetid is updated, the function sends the new assetid as val and key = 'assetid'
   * If src is updated, the function sends the new external URL src as val and key = 'src'
   */
  updateAssetValue?: Function;
  /**
   * existing externalUrl, may be empty
   */
  externalUrl: string;
  /**
   * add static images with public access (no S3 URL). Default: false
   */
  addPublicAssets?: boolean;
  /**
   * sets AssetMenu height to 100%
   */
  fullHeight?: boolean;
}

export const AssetMenu: React.FC<AssetMenuProps> = ({
  assetType,
  updateAssetValue,
  externalUrl = '',
  addPublicAssets = false,
  fullHeight = false,
}) => {
  const intl = useIntl();

  // manage asset menu
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const assetMenuOpen = Boolean(anchorEl);

  const handleMenuButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const [isAssetUploadOpen, setIsAssetUploadOpen] = useState<boolean>(false);
  const [isAssetSelectOpen, setIsAssetSelectOpen] = useState<boolean>(false);
  const [isExternalUrlSelectOpen, setIsExternalUrlSelectOpen] =
    useState<boolean>(false);

  const handleMenuClose = (menuItem: number | undefined) => {
    if (menuItem !== undefined) {
      if (menuItem === 1) {
        setIsAssetUploadOpen(true);
      }
      if (menuItem === 2) {
        setIsAssetSelectOpen(true);
      }
      if (menuItem === 3) {
        setIsExternalUrlSelectOpen(true);
      }
      if (menuItem === 4) {
        localUpdatePayloadValue(CardImageEnum.acceptance, 'image');
        localUpdatePayloadValue('', 'assetid');
        localUpdatePayloadValue('', 'src');
      }
    }
    setAnchorEl(null);
  };

  //
  // asset was set by upload or by asset selector
  //
  const onUpdateAsset = (asset: Asset) => {
    if (!asset) {
      return;
    }

    // Set external URL, when asset is a public default asset.
    // Find setup of these hard coded files in AssetHelper.ts
    if (asset.metadata.externalUrl && asset.metadata.externalUrl !== '') {
      // set image for externalUrls from metadata to enable proper sizing in ObjectCard
      localUpdatePayloadValue(CardImageEnum.acceptance, 'image');
      localUpdatePayloadValue(asset.metadata.externalUrl, 'src');
    } else {
      // order of updates is crucial
      localUpdatePayloadValue(undefined, 'image');
      localUpdatePayloadValue(asset.assetId, 'assetid');
    }
  };

  //
  // if the payload update sequence is defined, call it with key+val
  // key  = assetid | src, val = new value
  //
  const localUpdatePayloadValue = (
    val: string | undefined | CardImageEnum,
    key: string
  ) => {
    if (updateAssetValue) {
      updateAssetValue(val, key);
    }
  };

  return (
    <>
      <EvoachButton
        sx={fullHeight ? { height: '100%' } : {}}
        id="basic-button"
        aria-controls={assetMenuOpen ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={assetMenuOpen ? 'true' : undefined}
        onClick={handleMenuButtonClick}
      >
        {intl.formatMessage({
          id: 'builder.editor.assetselectinput.menubutton',
          defaultMessage: 'Datei ändern',
        })}
        &nbsp;&nbsp;&nbsp;&#x25BC;
      </EvoachButton>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={assetMenuOpen}
        onClose={() => handleMenuClose(undefined)}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        <MenuItem onClick={() => handleMenuClose(1)}>
          {intl.formatMessage({
            id: 'builder.editor.assetselectinput.menu.item1',
            defaultMessage: 'Datei hochladen',
          })}
        </MenuItem>
        <MenuItem onClick={() => handleMenuClose(2)}>
          {intl.formatMessage({
            id: 'builder.editor.assetselectinput.menu.item2',
            defaultMessage: 'Datei aus Liste wählen',
          })}
        </MenuItem>
        <MenuItem onClick={() => handleMenuClose(3)}>
          {intl.formatMessage({
            id: 'builder.editor.assetselectinput.menu.item3',
            defaultMessage: 'Externe URL angeben',
          })}
        </MenuItem>
        <MenuItem onClick={() => handleMenuClose(4)}>
          {intl.formatMessage({
            id: 'builder.editor.assetselectinput.menu.item4',
            defaultMessage: 'Datei zurücksetzen',
          })}
        </MenuItem>
      </Menu>
      <AssetUploadDialog
        isOpen={isAssetUploadOpen}
        onClose={() => setIsAssetUploadOpen(false)}
        onUpload={(asset: Asset) => {
          setIsAssetUploadOpen(false);
          onUpdateAsset(asset);
        }}
      />
      <AssetSelectorDialog
        isOpen={isAssetSelectOpen}
        onClose={() => setIsAssetSelectOpen(false)}
        onSelect={(asset: Asset) => {
          setIsAssetSelectOpen(false);
          onUpdateAsset(asset);
        }}
        assetType={assetType}
        addPublicAssets={addPublicAssets}
      />
      <AssetExternalSourceDialog
        isOpen={isExternalUrlSelectOpen}
        src={externalUrl}
        onClose={() => setIsExternalUrlSelectOpen(false)}
        onUpdate={(newurl: string) => {
          setIsExternalUrlSelectOpen(false);
          // if it is a real external URL, reset image
          localUpdatePayloadValue(undefined, 'image');
          localUpdatePayloadValue(newurl, 'src');
        }}
        assetType={assetType}
      />
    </>
  );
};
